import React, { useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';
import { SelectField } from '@/components/dropdowns/SelectField/SelectField';
import { SelectItem } from '@/components/ui/select';
import { PrimaryButton } from '@/components/buttons/PrimaryButton/PrimaryButton';
import { SecondaryButton } from '@/components/buttons/SecondaryButton/SecondaryButton';
import { ROOT_NODE, useEditor, useNode, type NodeId } from '@craftjs/core';
import { useAppSelector } from '@/store/hooks';
import { selectActiveGlobalStyle } from '@/store/globalStyles/selectors';
import type { ButtonProps } from '@builder4/shared/components/Button';
import type { InteractionEvent } from '@builder4/shared/types/craft';
import {
  isButtonNode,
  isCountdownNode,
  isImageNode,
  isTextBlockNode,
  isPopupNode,
} from '@/utils/helperFunc';
import {
  LINK_TO_PAGE_EVENT,
  JUMP_TO_SECTION_EVENT,
  EXTERNAL_LINK_EVENT,
  NO_LINK_EVENT,
  MIXED_LINK_EVENT,
  DEFAULT_INTERACTION_EVENT,
  OPEN_POPUP_EVENT,
} from '@builder4/shared/utils/constants';
import { applyMark, getGlobalLinkStyle, getMark } from '@/utils/textUtils';
import { editorRegistry } from '@/components/userComponents/Text';
import { Editor as SlateEditor, Range, Text, Transforms, type Path, type BaseRange } from 'slate';
import type { CustomText, SlateEditorType } from '@builder4/shared/types/text';
import type { LinkVariant, StylesConfig } from '@builder4/shared/types/styles';
import {
  useSlateSelectionChange,
  type SelectionChangePayload,
} from '@/hooks/useSlateSelectionChange';
import { ReactEditor } from 'slate-react';
import { useInteractionEvent, type LinkOptionsType } from '@/hooks/useInteractionEvent';
import {
  PageSelectField,
  ExternalUrlField,
  LinkOptionsFields,
  SectionSelectField,
  PopupSelectField,
} from '@/components/dropdowns/shared';
import type { PopupProps } from '@builder4/shared/components/Popup';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import { useCraftHelper } from '@/hooks/useCraftHelper';
import { useScrollNodeIntoView } from '@/hooks/useScrollNodeIntoView';
import { selectIsFeatureEnabled } from '@/store/featureFlags/selectors';

interface EventProps {
  className: string;
  onClick?: () => void;
  text: string;
  selectValue?: string;
}

type LinkLeafNode = {
  id: string;
  path: Path;
  text: string;
  variant?: LinkVariant;
  clickEvent?: InteractionEvent | null;
};

const DELETE_LINK_EVENTS_OPTION = 'delete-linked-events';

export const Event: React.FC<EventProps> = ({ className = '', onClick, text = 'Event' }) => {
  const { query, actions: editorActions } = useEditor();
  const {
    appliedGlobalStyle,
    id,
    node,
    props,
    displayName,
    actions: { setProp },
  } = useNode((node) => {
    return {
      node,
      props: node.data.props,
      displayName: node.data.displayName,
      appliedGlobalStyle: node.data.props.appliedGlobalStyle,
    };
  });

  const { addPopup } = useCraftHelper();
  const scrollNodeIntoView = useScrollNodeIntoView();
  const { setBPProp, getBPProp } = useBreakpointAwareProp(displayName);
  const isPopupEnabled = useAppSelector(selectIsFeatureEnabled('page-studio-popup'));

  const clickEvent = getBPProp(props, 'clickEvent');

  const activeGlobalStyle = useAppSelector(selectActiveGlobalStyle);
  const pageNode = query.node(ROOT_NODE).get();
  const children = pageNode.data.nodes;

  // Exclude nodes that are pop-ups
  const [sections] = useState<{ id: NodeId; label: string }[]>(
    children
      .map((child) => query.node(child).get())
      .filter((childNode) => !isPopupNode(childNode))
      .map((filteredNode) => ({
        id: filteredNode.id,
        label: filteredNode.data.custom.displayName,
      })),
  );

  // Get all popup nodes on the page
  const popups = useMemo(() => {
    return children
      .map((childId) => query.node(childId).get())
      .filter((childNode) => isPopupNode(childNode))
      .map((popupNode) => ({
        id: popupNode.id,
        label: popupNode.data.custom.displayName || 'Popup',
      }));
  }, [children, query]);

  const [linkLeaves, setLinkLeaves] = useState<LinkLeafNode[]>([]);

  const getCurrentMarkInteractionEvent = useCallback(() => {
    const slateEditor = editorRegistry.get(id);
    if (!slateEditor) return null;
    const { editor } = slateEditor;

    const selection = editor.selection;
    const leaves = collectLinkLeaves(editor, selection);
    if (leaves.length > 1) {
      return {
        eventType: MIXED_LINK_EVENT,
        eventData: null,
      };
    }
    const marks = SlateEditor.marks(editor);
    if (marks && Object.keys(marks).length > 0) {
      return marks['clickEvent'] as InteractionEvent | null;
    }
    return null;
  }, [id]);

  // Determine which event to use based on node type
  // For text nodes, we add linkLeaves as a dependency to ensure re-computation when marks change
  const currentEvent = useMemo(() => {
    if (isButtonNode(node) || isImageNode(node) || isCountdownNode(node)) {
      return clickEvent;
    } else if (isTextBlockNode(node)) {
      const textEvent = getCurrentMarkInteractionEvent();
      return textEvent || DEFAULT_INTERACTION_EVENT;
    }
    return null;
  }, [node, clickEvent, linkLeaves, getCurrentMarkInteractionEvent]);

  const {
    eventType,
    optionId,
    externalUrl,
    linkOptions,
    pages,
    isPagesLoading,
    constructLinkedPageUrl,
  } = useInteractionEvent({
    event: currentEvent,
    defaultEventType: NO_LINK_EVENT,
  });

  const handleTextSelectionChange = useCallback(
    ({ editorId, editor, selection }: SelectionChangePayload) => {
      if (editorId === id) {
        if (isTextBlockNode(node)) {
          setLinkLeaves(collectLinkLeaves(editor, selection));
        }
      }
    },
    [id, node],
  );

  useSlateSelectionChange(handleTextSelectionChange);

  //no sure why its here?
  const handleClick = () => {
    if (onClick) onClick();
  };
  const handleChangeEventType = (newValue: string) => {
    let optionIdValue = '';
    if (newValue === OPEN_POPUP_EVENT && popups.length === 0) {
      const newPopupId = addPopup();
      if (newPopupId) {
        optionIdValue = newPopupId;
      }
    }

    const data =
      newValue === NO_LINK_EVENT
        ? null
        : {
            url: '',
            optionId: optionIdValue,
            newTab: false,
            useNofollow: false,
          };
    const newClickEvent = {
      eventType: newValue,
      eventData: data,
    } as InteractionEvent;
    if (isButtonNode(node) || isImageNode(node) || isCountdownNode(node)) {
      setProp((props: Partial<ButtonProps>) => {
        //reset the event data
        setBPProp(props, 'clickEvent', newClickEvent);
      });
    } else if (isTextBlockNode(node)) {
      if (newValue === NO_LINK_EVENT) {
        applyTextLinkVariant(undefined);
      } else {
        //check if the node has a selected link variant
        // if no then set the default link variant
        const selectedLinkVariant = getSelectedLinkVariant();
        applyTextLinkVariant(selectedLinkVariant || 'link1');
      }
      applyClickEvent(newClickEvent);
    }
  };

  const getSelectedLinkVariant = () => {
    const slateEditor = editorRegistry.get(id);
    if (!slateEditor) return undefined;
    const { editor } = slateEditor;
    const selectedLinkVariant = getMark(editor, 'selectedLinkVariant') as LinkVariant | undefined;
    return selectedLinkVariant;
  };

  const handleSectionChange = (sectionId: NodeId) => {
    if (isButtonNode(node) || isImageNode(node) || isCountdownNode(node)) {
      setProp((props: Partial<ButtonProps>) => {
        const currentClickEvent = getBPProp(props, 'clickEvent');
        setBPProp(props, 'clickEvent', {
          ...currentClickEvent,
          eventData: {
            ...(currentClickEvent?.eventData || {}),
            url: `#${sectionId}`,
            optionId: sectionId,
          },
        } as InteractionEvent);
      });
    } else if (isTextBlockNode(node)) {
      const currentClickEvent = getCurrentMarkInteractionEvent();
      if (currentClickEvent) {
        applyClickEvent({
          ...currentClickEvent,
          eventData: {
            ...(currentClickEvent.eventData || {}),
            optionId: sectionId,
            url: `#${sectionId}`,
          },
        } as InteractionEvent);
      }
    }
  };

  const handlePopupChange = (popupId: NodeId) => {
    if (isButtonNode(node) || isImageNode(node) || isCountdownNode(node)) {
      setProp((props: Partial<ButtonProps>) => {
        const currentClickEvent = getBPProp(props, 'clickEvent');
        setBPProp(props, 'clickEvent', {
          ...currentClickEvent,
          eventData: {
            ...(currentClickEvent?.eventData || {}),
            optionId: popupId,
          },
        } as InteractionEvent);
      });
    } else if (isTextBlockNode(node)) {
      const currentClickEvent = getCurrentMarkInteractionEvent();
      if (currentClickEvent) {
        applyClickEvent({
          ...currentClickEvent,
          eventData: {
            ...(currentClickEvent.eventData || {}),
            optionId: popupId,
          },
        } as InteractionEvent);
      }
    }
  };

  const handleEditPopup = (popupId: string) => {
    // Open the popup by setting isOpen to true
    editorActions.setProp(popupId, (props: PopupProps) => {
      props.isOpen = true;
    });
    // Select the popup node
    editorActions.selectNode(popupId);
    scrollNodeIntoView(popupId, true);
  };

  const handleCreatePopup = () => {
    const newPopupId = addPopup();
    if (newPopupId) {
      handlePopupChange(newPopupId);
      scrollNodeIntoView(newPopupId, true);
    }
  };

  const handlePageChange = (pageId: string) => {
    if (isButtonNode(node) || isImageNode(node) || isCountdownNode(node)) {
      setProp((props: Partial<ButtonProps>) => {
        const currentClickEvent = getBPProp(props, 'clickEvent');
        setBPProp(props, 'clickEvent', {
          ...currentClickEvent,
          eventData: {
            ...(currentClickEvent?.eventData || {}),
            optionId: pageId,
            url: constructLinkedPageUrl(pageId),
          },
        } as InteractionEvent);
      });
    } else if (isTextBlockNode(node)) {
      const currentClickEvent = getCurrentMarkInteractionEvent();
      if (currentClickEvent) {
        applyClickEvent({
          ...currentClickEvent,
          eventData: {
            ...(currentClickEvent.eventData || {}),
            optionId: pageId,
            url: constructLinkedPageUrl(pageId),
          },
        });
      }
    }
  };

  const handleChangeUrl = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (isButtonNode(node) || isImageNode(node) || isCountdownNode(node)) {
      setProp((props: Partial<ButtonProps>) => {
        const currentClickEvent = getBPProp(props, 'clickEvent');
        setBPProp(props, 'clickEvent', {
          ...currentClickEvent,
          eventData: {
            ...(currentClickEvent?.eventData || {}),
            url: value,
          },
        } as InteractionEvent);
      });
    } else if (isTextBlockNode(node)) {
      const currentClickEvent = getCurrentMarkInteractionEvent();
      if (currentClickEvent) {
        applyClickEvent(
          {
            ...currentClickEvent,
            eventData: {
              ...(currentClickEvent.eventData || {}),
              url: value,
            },
          },
          false,
        );
      }
    }
  };

  const handleSanitizeUrl = (e: React.FocusEvent<HTMLInputElement>) => {
    let value = e.target.value.trim();
    if (value && !value.startsWith('http')) {
      value = `https://${value}`;
    }

    if (isButtonNode(node) || isImageNode(node) || isCountdownNode(node)) {
      setProp((props: Partial<ButtonProps>) => {
        const currentClickEvent = getBPProp(props, 'clickEvent');
        setBPProp(props, 'clickEvent', {
          ...currentClickEvent,
          eventData: {
            ...(currentClickEvent?.eventData || {}),
            url: value,
          },
        } as InteractionEvent);
      });
    } else if (isTextBlockNode(node)) {
      const currentClickEvent = getCurrentMarkInteractionEvent();
      if (currentClickEvent) {
        applyClickEvent(
          {
            ...currentClickEvent,
            eventData: {
              ...(currentClickEvent.eventData || {}),
              url: value,
            },
          } as InteractionEvent,
          false,
        );
      }
    }
  };

  const toggleLinkedPageLinkOptions = (option: string) => {
    if (isButtonNode(node) || isImageNode(node) || isCountdownNode(node)) {
      setProp((props: Partial<ButtonProps>) => {
        const currentClickEvent = getBPProp(props, 'clickEvent');
        setBPProp(props, 'clickEvent', {
          ...currentClickEvent,
          eventData: {
            ...(currentClickEvent?.eventData || {}),
            [option]: !currentClickEvent?.eventData?.[option as keyof LinkOptionsType],
          },
        } as InteractionEvent);
      });
    } else if (isTextBlockNode(node)) {
      const currentClickEvent = getCurrentMarkInteractionEvent();
      if (currentClickEvent) {
        applyClickEvent({
          ...currentClickEvent,
          eventData: {
            ...(currentClickEvent.eventData || {}),
            [option]: !currentClickEvent.eventData?.[option as keyof LinkOptionsType],
          },
        } as InteractionEvent);
      }
    }
  };

  const applyTextLinkVariant = (selectedLinkVariant: LinkVariant | undefined) => {
    const slateEditor = editorRegistry.get(id);
    if (!slateEditor) return;
    const { editor } = slateEditor;
    selectLinkText(editor);
    applyMark(editor, 'selectedLinkVariant', selectedLinkVariant);
    if (!appliedGlobalStyle && selectedLinkVariant) {
      applyDetachedLinkVariantMarks(editor, selectedLinkVariant, activeGlobalStyle);
    }
  };

  const selectLinkText = (editor: SlateEditorType) => {
    //select leaf node if the text is not selected
    const { selection } = editor;

    if (selection && Range.isCollapsed(selection)) {
      const [textEntry] = SlateEditor.nodes(editor, {
        at: selection,
        match: Text.isText,
      });

      if (textEntry) {
        const [node, textPath] = textEntry;
        const textNode = node as CustomText;
        if (textNode.selectedLinkVariant) {
          const textRange = SlateEditor.range(editor, textPath);
          Transforms.select(editor, textRange);
          ReactEditor.focus(editor);
        } else {
          const fullRange = SlateEditor.range(editor, []);
          Transforms.select(editor, fullRange);
          ReactEditor.focus(editor);
        }
      }
    }
  };

  const applyClickEvent = (clickEvent: InteractionEvent, shouldSelectText = true) => {
    const slateEditor = editorRegistry.get(id);
    if (!slateEditor) return;
    const { editor } = slateEditor;
    if (shouldSelectText) {
      selectLinkText(editor);
    }
    applyMark(editor, 'clickEvent', clickEvent);
  };

  const handleSelectLeafNodeEvent = useCallback(
    (value: string) => {
      if (value === MIXED_LINK_EVENT) {
        return;
      }

      const slateEditor = editorRegistry.get(id);
      if (!slateEditor) {
        return;
      }

      const { editor } = slateEditor;

      if (value === DELETE_LINK_EVENTS_OPTION) {
        clearLinkEvents(editor, linkLeaves);
        setLinkLeaves([]);
        return;
      }

      const leaf = linkLeaves.find((item) => item.id === value);
      if (!leaf) {
        return;
      }

      const leafRange = SlateEditor.range(editor, leaf.path);
      Transforms.select(editor, leafRange);
      ReactEditor.focus(editor);
      setLinkLeaves(collectLinkLeaves(editor, editor.selection));
    },
    [id, linkLeaves],
  );

  return (
    <div
      onClick={handleClick}
      className={clsx('flex w-full flex-col items-start gap-5', className)}
    >
      <div className="flex w-full items-center justify-between gap-3">
        {text !== '' && <p className="text-button-sm text-neutral-500">{text}</p>}
        {eventType === MIXED_LINK_EVENT ? (
          <SelectField
            className="w-full max-w-[160px]"
            size="large"
            defaultValue={eventType}
            onValueChange={handleSelectLeafNodeEvent}
            placeholder=""
            text=""
          >
            <SelectItem className="hidden" value={MIXED_LINK_EVENT}>
              Mixed
            </SelectItem>
            {linkLeaves.map((leaf, index) => (
              <SelectItem key={leaf.id} value={leaf.id}>
                {leaf.text?.trim() || `Link ${index + 1}`}
              </SelectItem>
            ))}
            {linkLeaves.length > 0 && (
              <SelectItem className="text-coral-500 text-sm" value={DELETE_LINK_EVENTS_OPTION}>
                Delete events
              </SelectItem>
            )}
          </SelectField>
        ) : (
          <SelectField
            className="w-full max-w-[160px]"
            size="large"
            defaultValue={eventType}
            onValueChange={handleChangeEventType}
            placeholder=""
            text=""
          >
            <SelectItem value={NO_LINK_EVENT}>No link option</SelectItem>
            <SelectItem value={LINK_TO_PAGE_EVENT}>Link to a page</SelectItem>
            <SelectItem value={JUMP_TO_SECTION_EVENT}>Jump to a section</SelectItem>
            {isPopupEnabled && <SelectItem value={OPEN_POPUP_EVENT}>Open a pop-up</SelectItem>}
            {/* <SelectItem value="calendly-popup">Calendly pop-up</SelectItem> */}
            <SelectItem value={EXTERNAL_LINK_EVENT}>Open external link</SelectItem>
          </SelectField>
        )}
      </div>

      {eventType === LINK_TO_PAGE_EVENT && (
        <div className="flex w-full flex-col items-start gap-3">
          <PageSelectField
            optionId={optionId}
            pages={pages}
            isPagesLoading={isPagesLoading}
            onPageChange={handlePageChange}
          />

          <LinkOptionsFields
            linkOptions={linkOptions}
            onToggleOption={toggleLinkedPageLinkOptions}
          />
        </div>
      )}

      {eventType === JUMP_TO_SECTION_EVENT && (
        <div className="flex w-full flex-col items-start gap-3">
          <SectionSelectField
            optionId={optionId}
            sections={sections}
            onSectionChange={handleSectionChange}
          />
        </div>
      )}

      {eventType === OPEN_POPUP_EVENT && (
        <div className="flex w-full flex-col items-start gap-3">
          <PopupSelectField optionId={optionId} popups={popups} onPopupChange={handlePopupChange} />
          {optionId && popups.some((popup) => popup.id === optionId) && (
            <SecondaryButton
              icon={null}
              className="w-full"
              size="sm"
              state="default"
              text="Edit Pop-up"
              onClick={() => handleEditPopup(optionId)}
            />
          )}
          <PrimaryButton
            icon={null}
            className="w-full"
            size="sm"
            state="default"
            text="Add New Pop-up"
            onClick={handleCreatePopup}
          />
        </div>
      )}

      {eventType === EXTERNAL_LINK_EVENT && (
        <div className="flex w-full flex-col items-start gap-3">
          <ExternalUrlField
            externalUrl={externalUrl}
            onChangeUrl={handleChangeUrl}
            onSanitizeUrl={handleSanitizeUrl}
          />

          <SecondaryButton
            icon={null}
            className="w-full"
            size="sm"
            state={externalUrl ? 'default' : 'disabled'}
            text="Visit URL"
            onClick={() => {
              if (externalUrl) {
                window.open(externalUrl, '_blank');
              }
            }}
          />

          <LinkOptionsFields
            linkOptions={linkOptions}
            onToggleOption={toggleLinkedPageLinkOptions}
          />

          {/* @todo: Uncomment when this feature is decided */}
          {/* <div className="flex items-center space-x-2">
            <Checkbox
              id="count-button"
              checked={externalOptions.countButton}
              onCheckedChange={() => {
                toggleExternalLinkOptions('countButton');
              }}
            />
            <Label className="inline text-xs text-neutral-500" htmlFor="count-button">
              <span>
                Count this button click as a page conversion.{' '}
                <a href="#" className="font-medium text-indigo-500 underline">
                  Learn more
                </a>
              </span>
            </Label>
          </div> */}
        </div>
      )}
      {isTextBlockNode(node) &&
        (eventType === LINK_TO_PAGE_EVENT ||
          eventType === JUMP_TO_SECTION_EVENT ||
          eventType === EXTERNAL_LINK_EVENT ||
          eventType === OPEN_POPUP_EVENT) && (
          <SelectField
            text="Link Style"
            className="w-full max-w-[216px]"
            defaultValue={getSelectedLinkVariant() || 'link1'}
            onValueChange={(value) => applyTextLinkVariant(value as LinkVariant)}
          >
            <SelectItem value="link1">Link Style 1</SelectItem>
            <SelectItem value="link2">Link Style 2</SelectItem>
          </SelectField>
        )}
      {/* @todo: Uncomment when this feature is decided */}
      {/* 
      {eventType === 'calendly-popup' && (
        <div className="flex w-full flex-col items-start gap-3">
          <div className="flex items-center gap-1">
            <p className="text-button-sm font-medium text-neutral-900">Position</p>
            <IconInformationCircle className="h-4 w-4 text-neutral-300" />
          </div>

          <TextField
            className="w-full max-w-[216px]"
            classNameInputParent="!w-full !max-w-[216px]"
            inputValue=""
            placeholderText="https://name.calendly.com..."
            state="default"
            text=""
            type="text"
          />

          <SecondaryButton
            icon={null}
            className="w-full"
            size="sm"
            state="default"
            text="Visit Calendly URL"
          />

          <div className="flex items-center gap-2 pt-2 pr-2 pb-2">
            <Checkbox id="pageConversation1" />
            <Label className="inline text-xs text-neutral-500" htmlFor="pageConversation1">
              <span>Count this button click as a page conversion.</span>{' '}
              <a href="#" className="font-medium text-indigo-500 underline">
                Learn more
              </a>
            </Label>
          </div>
        </div>
      )} */}
    </div>
  );
};

function applyDetachedLinkVariantMarks(
  editor: SlateEditorType,
  variant: LinkVariant,
  stylesConfig: StylesConfig,
) {
  const override = getGlobalLinkStyle(stylesConfig, variant);
  const selection = editor.selection;
  const nodes = SlateEditor.nodes(editor, {
    at: selection ?? [],
    match: Text.isText,
  });

  for (const [node, path] of nodes) {
    const textNode = node as CustomText;
    if (textNode.selectedLinkVariant !== variant) {
      continue;
    }

    Transforms.setNodes(editor, { ...override }, { at: path });
  }
}

function clearLinkEvents(editor: SlateEditorType, leaves: LinkLeafNode[]) {
  if (leaves.length === 0) {
    return;
  }

  SlateEditor.withoutNormalizing(editor, () => {
    leaves.forEach((leaf) => {
      Transforms.setNodes(
        editor,
        {
          selectedLinkVariant: undefined,
          clickEvent: undefined,
        },
        { at: leaf.path },
      );
    });
  });
}

function collectLinkLeaves(editor: SlateEditorType, selection: BaseRange | null): LinkLeafNode[] {
  const leaves: LinkLeafNode[] = [];

  for (const [node, path] of SlateEditor.nodes(editor, {
    at: selection ?? [],
    match: Text.isText,
  })) {
    const textNode = node as CustomText;
    if (!textNode.selectedLinkVariant) {
      continue;
    }
    leaves.push({
      id: path.join('-'),
      path,
      text: textNode.text,
      variant: textNode.selectedLinkVariant,
      clickEvent: textNode.clickEvent,
    });
  }

  return leaves;
}
